import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';
import { IMspConfigAllowedUser, IMspConfigApprovalType } from 'src/app/features/managed-services/models/msp-config';
import { ManagedServicesConfigService } from 'src/app/features/managed-services/services/managed-services-config.service';
import { ManagedServicesRepository } from 'src/app/features/managed-services/services/managed-services-repository.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { IChipsAutocomplete, IChipsOption } from 'src/app/shared/models/chips-autocomplete';
import { ModuleApprovalType, ModuleId, MspConfigCardState } from '../../../types/msp-config';

@Component({
  selector: 'msp-config-module-approval',
  templateUrl: './msp-config-module-approval.component.html',
  styleUrls: ['./msp-config-module-approval.component.scss'],
  standalone: false,
})
export class MspConfigModuleApprovalComponent implements OnInit {
  // Card state
  cardState: MspConfigCardState = 'view';

  // Change management approvers list
  moduleApprovers: IMspConfigAllowedUser[] = [];

  // Module approval type
  moduleApprovalType: IMspConfigApprovalType;

  // Allow to override the module approval
  moduleAllowToOverride: boolean;

  selectedApproversId: number[] = [];

  // List of allowed users for the MSP Config autocomplete
  autocompleteUsersOptions: IChipsAutocomplete[] = [];

  // Controls Input names
  allowOverrideInputName = 'allowOverride';
  approvalTypeInputName = 'approvalType';

  // Checkbox title
  checkboxTitle = 'Allow users to override';

  // Allowed domains form
  approvalForm: UntypedFormGroup = new UntypedFormGroup({
    [this.allowOverrideInputName]: new UntypedFormControl(false),
    [this.approvalTypeInputName]: new UntypedFormControl(),
  });

  // Radio group options
  radioGroupOptions: { name: string; value: ModuleApprovalType }[] = [
    {
      value: 'any-order',
      name: 'Any order',
    },
    {
      value: 'in-sequence',
      name: 'In sequence',
    },
    {
      value: 'any-one-user',
      name: 'Any one user',
    },
  ];

  /**
   * Module type of the card
   */
  @Input()
  moduleId: ModuleId = 'change-management';

  /**
   * Title of the card
   */
  @Input()
  title = 'Change Management Approvers';

  /**
   * Icon of the card
   */
  @Input()
  icon = 'approval_delegation';

  /**
   * True if the user can edit this card
   */
  @Input()
  canEdit: boolean = true;

  constructor(
    private managedServicesRepository: ManagedServicesRepository,
    private snackBar: MatSnackBar,
    private managedServicesConfigService: ManagedServicesConfigService,
    private matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.getModuleApprovalData();
    this.getAllowedUsers();
  }

  /**
   * Get module approval data from the API
   */
  getModuleApprovalData(): void {
    this.cardState = 'loading';
    this.managedServicesRepository.getMspConfigModuleApproval(this.moduleId).subscribe({
      next: (response) => {
        if (response) {
          this.moduleApprovers = response.approvers.sort((a, b) => a.priority - b.priority);
          this.moduleApprovalType = response.approvalType;
          this.moduleAllowToOverride = response.allowOverride;

          this.approvalForm.get(this.allowOverrideInputName)?.setValue(this.moduleAllowToOverride);
          this.approvalForm.get(this.approvalTypeInputName)?.setValue(this.moduleApprovalType.id);
        }
        this.cardState = 'view';
      },
      error: (error) => {
        console.error(error);
        this.snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: `Error fetching module approval data`,
            closeButton: true,
          },
          duration: 5000,
        });
      },
    });
  }

  /**
   * Check if the save button should be disabled
   * @returns True if the save button should be disabled
   */
  isSaveDisabled(): boolean {
    return (
      this.cardState === 'saving' ||
      this.selectedApproversId.length === 0 ||
      (this.approvalForm.get(this.approvalTypeInputName)?.value === this.moduleApprovalType?.id &&
        this.approvalForm.get(this.allowOverrideInputName)?.value === this.moduleAllowToOverride &&
        JSON.stringify(this.selectedApproversId) === JSON.stringify(this.moduleApprovers.map((user) => user.id)))
    );
  }

  onCancel(): void {
    this.cardState = 'view';
  }

  onEdit(): void {
    this.selectedApproversId = this.moduleApprovers.map((user) => user.id);
    this.approvalForm.get(this.allowOverrideInputName)?.setValue(this.moduleAllowToOverride === true);

    // Set the default value for the radio group
    if (this.moduleApprovalType?.id === undefined) {
      this.moduleApprovalType = {
        id: this.radioGroupOptions[0].value,
        name: this.radioGroupOptions[0].name,
      };
    }
    this.approvalForm.get(this.approvalTypeInputName)?.setValue(this.moduleApprovalType?.id);
    this.cardState = 'edit';
  }

  /**
   * Save the module approval data.
   * Show an acknowledgement dialog if approval type or approvers are changed that it will take effect after the next ticket is created.
   */
  onSave(): void {
    const onlyAllowOverrideChanged =
      this.approvalForm.get(this.approvalTypeInputName)?.value === this.moduleApprovalType?.id &&
      JSON.stringify(this.selectedApproversId) === JSON.stringify(this.moduleApprovers.map((user) => user.id)) &&
      this.approvalForm.get(this.allowOverrideInputName)?.value !== this.moduleAllowToOverride;

    if (onlyAllowOverrideChanged) {
      this.saveModuleApprovalData();
      return;
    }

    this.matDialog.open(ConfirmDialogComponent, {
      maxWidth: '700px',
      minHeight: '210px',
      data: {
        title: 'Important',
        message: 'This updated list of approvers or approval type only applies to ticket created after saving.',
        confirmAction: () => {
          this.saveModuleApprovalData();
        },
        autoCloseOnConfirm: true,
        confirmButtonMessage: 'Acknowledge',
        closeButtonMessage: '',
      },
    });
  }

  /**
   * Returns the selected users
   * @returns IChipsOption[]
   */
  getSelectedUsers(): IChipsOption[] {
    return this.moduleApprovers.map((user) => {
      return {
        id: user.id,
        name: user.fullName,
        tooltipText: user.email,
      };
    });
  }

  /**
   * Fetches the allowed users for the MSP Config autocomplete input
   * @returns void
   */
  getAllowedUsers() {
    this.autocompleteUsersOptions = [];
    this.managedServicesConfigService.allowedUsersConfig$.subscribe({
      next: (users) => {
        this.autocompleteUsersOptions = users?.map((user) => ({
          id: user.id,
          name: user.fullName,
          displayAutoCompleteName: user.fullName ? user.fullName + ' (' + user.email + ')' : user.email,
          tooltipText: user.email,
        }));
      },
      error: (error) => {
        console.error(error);
        this.snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: `Error fetching msp configuration allowed users`,
            closeButton: true,
          },
          duration: 5000,
        });
      },
    });
  }

  /**
   * On option selected
   * @param selectedOptions - The selected options
   */
  optionSelected(selectedOptions: IChipsOption[]) {
    this.selectedApproversId = selectedOptions.map((option, index) => {
      return option.id as number;
    });
  }

  /**
   * Save the module approval data
   */
  saveModuleApprovalData(): void {
    this.cardState = 'saving';
    this.approvalForm.get(this.approvalTypeInputName)?.disable();
    this.approvalForm.get(this.allowOverrideInputName)?.disable();
    const data = {
      moduleId: this.moduleId,
      approvalType: this.approvalForm.get(this.approvalTypeInputName)?.value,
      allowOverride: this.approvalForm.get(this.allowOverrideInputName)?.value,
      approvers: this.selectedApproversId,
    };

    this.managedServicesRepository.saveMspConfigModuleApproval(data).subscribe({
      next: () => {
        this.getModuleApprovalData();
        this.approvalForm.get(this.approvalTypeInputName)?.enable();
        this.approvalForm.get(this.allowOverrideInputName)?.enable();
        this.snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: `<p>Change management approvers successfully saved.</p>`,
            closeButton: true,
          },
          duration: 5000,
        });
      },
      error: (error) => {
        console.error(error);
        this.approvalForm.get(this.approvalTypeInputName)?.enable();
        this.approvalForm.get(this.allowOverrideInputName)?.enable();
        this.cardState = 'edit';
        this.snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: `Error saving module approval data`,
            closeButton: true,
          },
          duration: 5000,
        });
      },
    });
  }
}
